import React, { Suspense, lazy } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useEffect } from 'react';
import './App.css';

// ── Code Splitting — lazy load toutes les pages ──────────
const HomePage      = lazy(() => import('./pages/HomePage'));
const CarsPage      = lazy(() => import('./pages/CarsPage'));
const CarDetailPage = lazy(() => import('./pages/CarDetailPage'));
const ContactPage   = lazy(() => import('./pages/ContactPage'));
const NotFoundPage  = lazy(() => import('./pages/NotFoundPage'));
const PrivacyPage   = lazy(() => import('./pages/PrivacyPage'));
const TermsPage     = lazy(() => import('./pages/TermsPage'));

// Admin — chunk séparé chargé uniquement si /admin
const AdminLogin        = lazy(() => import('./admin/AdminLogin'));
const AdminLayout       = lazy(() => import('./admin/AdminLayout'));
const AdminDashboard    = lazy(() => import('./admin/AdminDashboard'));
const AdminCars         = lazy(() => import('./admin/AdminCars'));
const AdminCustomers    = lazy(() => import('./admin/AdminCustomers'));
const AdminReservations = lazy(() => import('./admin/AdminReservations'));
const AdminMessages     = lazy(() => import('./admin/AdminMessages'));
const AdminProfile      = lazy(() => import('./admin/AdminProfile'));

// ── Fallback de chargement ───────────────────────────────
function PageLoader() {
  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: 'var(--bg)',
    }}>
      <div style={{ textAlign: 'center' }}>
        <div style={{
          width: 40, height: 40,
          border: '3px solid var(--border)',
          borderTopColor: 'var(--accent)',
          borderRadius: '50%',
          animation: 'spin 0.8s linear infinite',
          margin: '0 auto',
        }} />
        <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
      </div>
    </div>
  );
}

// ── Route protégée Admin ─────────────────────────────────
function ProtectedRoute({ children }) {
  const admin = localStorage.getItem('admin');
  if (!admin) return <Navigate to="/admin/login" replace />;
  return children;
}

// ── Scroll en haut à chaque route ───────────────────────
function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    // requestAnimationFrame évite le forced reflow
    requestAnimationFrame(() => {
      window.scrollTo({ top: 0, behavior: 'instant' });
    });
  }, [pathname]);
  return null;
}

export default function App() {
  return (
    <>
      <ScrollToTop />
      <Suspense fallback={<PageLoader />}>
        <Routes>
          {/* Routes publiques */}
          <Route path="/"         element={<HomePage />} />
          <Route path="/cars"     element={<CarsPage />} />
          <Route path="/cars/:id" element={<CarDetailPage />} />
          <Route path="/contact"  element={<ContactPage />} />
          <Route path="/privacy"  element={<PrivacyPage />} />
          <Route path="/terms"    element={<TermsPage />} />

          {/* Admin */}
          <Route path="/admin/login" element={<AdminLogin />} />
          <Route path="/admin" element={
            <ProtectedRoute>
              <AdminLayout />
            </ProtectedRoute>
          }>
            <Route index               element={<AdminDashboard />} />
            <Route path="cars"         element={<AdminCars />} />
            <Route path="customers"    element={<AdminCustomers />} />
            <Route path="reservations" element={<AdminReservations />} />
            <Route path="messages"     element={<AdminMessages />} />
            <Route path="profile"      element={<AdminProfile />} />
          </Route>

          {/* 404 */}
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
      </Suspense>
    </>
  );
}
